import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { GoogleMap, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api'
import React, { useRef, useState } from 'react'
import {
  BottomNav,
  localizePlace,
  POPULAR_ROUTES,
  ProgressHeader,
  RequiredMark,
  TopBar,
} from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

export interface OneWayVehiclePrice {
  vehicleType: string
  price: number | null
}

export interface OneWayRouteEntry {
  id: string
  origin: string
  destination: string
  isPopular: boolean
  vehiclePrices: OneWayVehiclePrice[]
}

const GMAPS_LIBRARIES = ['places', 'geometry'] as any[]

type PricingRow = { vehicleType: string; price: string }

interface Props {
  routes: OneWayRouteEntry[]
  availableCarTypes: string[]
  currentStep: number
  totalSteps: number
  onExit: () => void
  onBack: () => void
  onNext: (routes: OneWayRouteEntry[]) => void
  onBeforeExit?: (routes: OneWayRouteEntry[]) => void
  submitting?: boolean
}

type View = 'list' | 'route-select' | 'custom-route' | 'pricing'

interface PendingRoute {
  id: string
  origin: string
  destination: string
  isPopular: boolean
}

const CarWithDriverStep3OneWay: React.FC<Props> = ({
  routes,
  availableCarTypes,
  currentStep,
  totalSteps,
  onExit,
  onBack,
  onNext,
  onBeforeExit,
  submitting = false,
}) => {
  const { lang, t } = useCwdLang()
  const place = (name: string) => localizePlace(name, lang)
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: GMAPS_LIBRARIES,
  })

  const [view, setView] = useState<View>('list')
  const [localRoutes, setLocalRoutes] = useState<OneWayRouteEntry[]>(routes)
  const [listError, setListError] = useState('')

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [selectError, setSelectError] = useState('')

  const [customOrigins, setCustomOrigins] = useState<string[]>([''])
  const [customDests, setCustomDests] = useState<string[]>([''])
  const [customError, setCustomError] = useState('')

  const [mapModal, setMapModal] = useState<{
    open: boolean
    forField: 'origin' | 'dest'
    forIdx: number
  }>({ open: false, forField: 'origin', forIdx: 0 })
  const [mapCenter, setMapCenter] = useState({ lat: 13.7563, lng: 100.5018 })
  const [mapLocationName, setMapLocationName] = useState('')
  const mapRef = useRef<google.maps.Map | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const modalSearchRef = useRef<google.maps.places.SearchBox | null>(null)

  const [pendingRoutes, setPendingRoutes] = useState<PendingRoute[]>([])
  const [routePricings, setRoutePricings] = useState<Record<string, PricingRow[]>>({})
  const [editingId, setEditingId] = useState<string | null>(null)
  const [pricingError, setPricingError] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const handleExit = () => {
    onBeforeExit?.(localRoutes)
    onExit()
  }

  const addedPopularIds = localRoutes
    .filter((r) => r.isPopular)
    .map(
      (r) =>
        POPULAR_ROUTES.find((p) => p.origin === r.origin && p.destination === r.destination)?.id,
    )
    .filter(Boolean) as string[]

  const availablePopular = POPULAR_ROUTES.filter((r) => !addedPopularIds.includes(r.id))

  const startPricing = (pending: PendingRoute[], editId: string | null = null) => {
    const pricings: Record<string, PricingRow[]> = {}
    pending.forEach((r) => {
      if (editId && editId === r.id) {
        const existing = localRoutes.find((lr) => lr.id === editId)
        if (existing) {
          const rows = existing.vehiclePrices
            .filter((vp) => vp.price !== null)
            .map((vp) => ({ vehicleType: vp.vehicleType, price: String(vp.price) }))
          pricings[r.id] = rows.length > 0 ? rows : [{ vehicleType: '', price: '' }]
        } else {
          pricings[r.id] = [{ vehicleType: '', price: '' }]
        }
      } else {
        pricings[r.id] = [{ vehicleType: '', price: '' }]
      }
    })
    setPendingRoutes(pending)
    setRoutePricings(pricings)
    setEditingId(editId)
    setPricingError('')
    setView('pricing')
  }

  const handleTogglePopular = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    setSelectError('')
  }

  const handleSelectNext = () => {
    if (selectedIds.length === 0) {
      setSelectError(t.oneWay.errSelectRoute)
      return
    }
    const pending = selectedIds.map((id) => {
      const r = POPULAR_ROUTES.find((p) => p.id === id)!
      return { id, origin: r.origin, destination: r.destination, isPopular: true }
    })
    startPricing(pending)
  }

  const handleCustomNext = () => {
    const validOrigins = customOrigins.filter((o) => o.trim())
    const validDests = customDests.filter((d) => d.trim())
    if (validOrigins.length === 0) {
      setCustomError(t.oneWay.errOrigin)
      return
    }
    if (validDests.length === 0) {
      setCustomError(t.oneWay.errDestination)
      return
    }
    setCustomError('')
    const pending: PendingRoute[] = []
    validOrigins.forEach((origin) => {
      validDests.forEach((dest) => {
        pending.push({
          id: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          origin,
          destination: dest,
          isPopular: false,
        })
      })
    })
    startPricing(pending)
  }

  const updateRow = (routeId: string, idx: number, field: keyof PricingRow, value: string) => {
    setRoutePricings((prev) => {
      const rows = [...(prev[routeId] || [])]
      rows[idx] = { ...rows[idx], [field]: value }
      return { ...prev, [routeId]: rows }
    })
    setPricingError('')
  }

  const addRow = (routeId: string) => {
    setRoutePricings((prev) => ({
      ...prev,
      [routeId]: [...(prev[routeId] || []), { vehicleType: '', price: '' }],
    }))
  }

  const removeRow = (routeId: string, idx: number) => {
    setRoutePricings((prev) => {
      const rows = (prev[routeId] || []).filter((_, i) => i !== idx)
      return { ...prev, [routeId]: rows.length > 0 ? rows : [{ vehicleType: '', price: '' }] }
    })
  }

  const handleConfirmPricing = () => {
    for (const route of pendingRoutes) {
      const valid = (routePricings[route.id] || []).filter((r) => r.vehicleType && r.price)
      if (valid.length === 0) {
        setPricingError(t.oneWay.errPricing(place(route.origin)))
        return
      }
    }
    const entries: OneWayRouteEntry[] = pendingRoutes.map((r) => ({
      id: r.id,
      origin: r.origin,
      destination: r.destination,
      isPopular: r.isPopular,
      vehiclePrices: (routePricings[r.id] || [])
        .filter((row) => row.vehicleType && row.price)
        .map((row) => ({ vehicleType: row.vehicleType, price: parseInt(row.price, 10) })),
    }))
    if (editingId) {
      setLocalRoutes((prev) => prev.map((r) => entries.find((e) => e.id === r.id) || r))
    } else {
      setLocalRoutes((prev) => [...prev, ...entries])
    }
    setView('list')
  }

  const handleEdit = (route: OneWayRouteEntry) => {
    startPricing(
      [
        {
          id: route.id,
          origin: route.origin,
          destination: route.destination,
          isPopular: route.isPopular,
        },
      ],
      route.id,
    )
  }

  const handleDelete = (id: string) => {
    setConfirmDeleteId(id)
  }

  const confirmDelete = () => {
    setLocalRoutes((prev) => prev.filter((r) => r.id !== confirmDeleteId))
    setConfirmDeleteId(null)
  }

  const handleListNext = () => {
    if (localRoutes.length === 0) {
      setListError(t.oneWay.errNoRoute)
      return
    }
    setListError('')
    onNext(localRoutes)
  }

  // ── Route Select ────────────────────────────────────────────────────
  if (view === 'route-select') {
    return (
      <div
        style={{
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: '#fff',
          minHeight: '100vh',
          maxWidth: '500px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <TopBar onExit={handleExit} />
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 4px' }}>
            {t.oneWay.popularTitle}
          </h2>
          <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 16px', lineHeight: '1.5' }}>
            {t.oneWay.popularDesc}
          </p>
          <div style={{ height: '1px', backgroundColor: '#F0F0F0', marginBottom: '12px' }} />
          {availablePopular.length === 0 ? (
            <p
              style={{ fontSize: '14px', color: '#667085', textAlign: 'center', marginTop: '32px' }}
            >
              {t.oneWay.allPopularAdded}
            </p>
          ) : (
            availablePopular.map((r) => {
              const checked = selectedIds.includes(r.id)
              return (
                <div
                  key={r.id}
                  onClick={() => handleTogglePopular(r.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 12px',
                    border: '1px solid #D6DAE0',
                    borderRadius: '10px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${checked ? '#0048B3' : '#D6DAE0'}`,
                      backgroundColor: checked ? '#0048B3' : '#fff',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {checked && (
                      <span
                        style={{
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: '700',
                          lineHeight: 1,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#212427',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {place(r.origin)}
                  </span>
                  <span style={{ fontSize: '16px', color: '#667085', flexShrink: 0 }}>⇄</span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '14px',
                      color: '#667085',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'right',
                    }}
                  >
                    {place(r.destination)}
                  </span>
                </div>
              )
            })
          )}
          {selectError && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '4px 0 8px' }}>{selectError}</p>
          )}
          <button
            onClick={() => {
              setCustomOrigins([''])
              setCustomDests([''])
              setCustomError('')
              setView('custom-route')
            }}
            style={{
              width: '100%',
              padding: '13px',
              backgroundColor: '#fff',
              color: '#0048B3',
              border: '1px dashed #0048B3',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              marginBottom: '20px',
            }}
          >
            {t.oneWay.addCustomRoute}
          </button>
        </div>
        <BottomNav
          onBack={() => setView('list')}
          onNext={handleSelectNext}
          nextLabel={t.oneWay.nextWithCount(selectedIds.length)}
        />
      </div>
    )
  }

  // ── Map Modal (inside custom-route) ─────────────────────────────────
  if (view === 'custom-route' && mapModal.open) {
    return (
      <div
        style={{
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: '#fff',
          minHeight: '100vh',
          maxWidth: '500px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <TopBar onExit={handleExit} />
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #F0F0F0' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 10px' }}>
            {mapModal.forField === 'origin' ? t.oneWay.selectStart : t.oneWay.selectDest}
          </h2>
          {isLoaded && window.google?.maps && (
            <StandaloneSearchBox
              onLoad={(ref) => {
                modalSearchRef.current = ref
              }}
              onPlacesChanged={() => {
                const places = modalSearchRef.current?.getPlaces()
                if (places?.[0]?.geometry?.location) {
                  const lat = places[0].geometry.location.lat()
                  const lng = places[0].geometry.location.lng()
                  const name = places[0].formatted_address || places[0].name || ''
                  setMapCenter({ lat, lng })
                  setMapLocationName(name)
                  mapRef.current?.panTo({ lat, lng })
                }
              }}
            >
              <input
                type="text"
                placeholder={t.oneWay.searchLocation}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid #D6DAE0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'Poppins, sans-serif',
                  outline: 'none',
                }}
              />
            </StandaloneSearchBox>
          )}
        </div>
        <div style={{ height: 'calc(100vh - 320px)', minHeight: '240px', position: 'relative' }}>
          {isLoaded && window.google?.maps && (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={14}
              options={{ disableDefaultUI: true, zoomControl: true }}
              onLoad={(map) => {
                mapRef.current = map
              }}
              onIdle={() => {
                const center = mapRef.current?.getCenter()
                if (!center) return
                const lat = center.lat()
                const lng = center.lng()
                if (!geocoderRef.current) {
                  geocoderRef.current = new google.maps.Geocoder()
                }
                geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
                  if (status === 'OK' && results?.[0]) {
                    setMapLocationName(results[0].formatted_address || '')
                  }
                })
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              fontSize: '36px',
              lineHeight: 1,
            }}
          >
            📍
          </div>
          <div
            style={{
              position: 'absolute',
              top: 'calc(50% + 6px)',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.65)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {t.oneWay.dragMapHint}
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #F0F0F0' }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#212427' }}>
            {t.oneWay.locationLabel}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#667085' }}>
            {mapLocationName || '...'}
          </p>
        </div>
        <BottomNav
          onBack={() => setMapModal((prev) => ({ ...prev, open: false }))}
          onNext={() => {
            if (!mapLocationName) return
            if (mapModal.forField === 'origin') {
              setCustomOrigins((prev) => {
                const updated = [...prev]
                updated[mapModal.forIdx] = mapLocationName
                return updated
              })
            } else {
              setCustomDests((prev) => {
                const updated = [...prev]
                updated[mapModal.forIdx] = mapLocationName
                return updated
              })
            }
            setMapModal((prev) => ({ ...prev, open: false }))
          }}
          nextLabel={t.common.confirm}
        />
      </div>
    )
  }

  // ── Custom Route ─────────────────────────────────────────────────────
  if (view === 'custom-route') {
    return (
      <div
        style={{
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: '#fff',
          minHeight: '100vh',
          maxWidth: '500px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <TopBar onExit={handleExit} />
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#212427', margin: '0 0 4px' }}>
            {t.oneWay.customTitle}
          </h2>
          <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 20px', lineHeight: '1.5' }}>
            {t.oneWay.customDesc}
          </p>

          <p style={{ fontSize: '14px', fontWeight: '600', color: '#212427', margin: '0 0 8px' }}>
            {t.oneWay.origin}
            <RequiredMark />
          </p>
          {customOrigins.map((origin, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}
            >
              <input
                type="text"
                placeholder={t.oneWay.selectOrigin}
                value={origin}
                readOnly
                onClick={() => {
                  setMapLocationName(origin)
                  setMapModal({ open: true, forField: 'origin', forIdx: idx })
                }}
                style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
              />
              {customOrigins.length > 1 && (
                <button
                  onClick={() => setCustomOrigins((prev) => prev.filter((_, i) => i !== idx))}
                  style={mapDeleteBtnStyle}
                >
                  <DeleteOutlined />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => setCustomOrigins((prev) => [...prev, ''])} style={addMoreBtnStyle}>
            {t.oneWay.addMoreOrigin}
          </button>

          <div
            style={{ textAlign: 'center', margin: '20px 0', color: '#667085', fontSize: '22px' }}
          >
            ⇅
          </div>

          <p style={{ fontSize: '14px', fontWeight: '600', color: '#212427', margin: '0 0 8px' }}>
            {t.oneWay.destination}
            <RequiredMark />
          </p>
          {customDests.map((dest, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}
            >
              <input
                type="text"
                placeholder={t.oneWay.selectDestination}
                value={dest}
                readOnly
                onClick={() => {
                  setMapLocationName(dest)
                  setMapModal({ open: true, forField: 'dest', forIdx: idx })
                }}
                style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
              />
              {customDests.length > 1 && (
                <button
                  onClick={() => setCustomDests((prev) => prev.filter((_, i) => i !== idx))}
                  style={mapDeleteBtnStyle}
                >
                  <DeleteOutlined />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => setCustomDests((prev) => [...prev, ''])} style={addMoreBtnStyle}>
            {t.oneWay.addMoreDestination}
          </button>

          {customError && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '12px 0 0' }}>{customError}</p>
          )}
        </div>
        <BottomNav
          onBack={() => setView('route-select')}
          onNext={handleCustomNext}
          nextLabel={t.oneWay.setPricing}
        />
      </div>
    )
  }

  // ── Pricing ──────────────────────────────────────────────────────────
  if (view === 'pricing') {
    return (
      <div
        style={{
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: '#fff',
          minHeight: '100vh',
          maxWidth: '500px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <TopBar onExit={handleExit} />
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 4px' }}>
            {t.oneWay.pricingTitle}
          </h2>
          <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 24px' }}>
            {t.oneWay.pricingDesc}
          </p>

          <div
            style={{
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 4px' }}>
              {t.common.netRevenueTitle}
            </p>
            <p style={{ fontSize: '13px', color: '#667085', margin: 0, lineHeight: '1.5' }}>
              {t.common.netRevenueDesc}
            </p>
          </div>

          {pendingRoutes.map((route, routeIdx) => {
            const rows = routePricings[route.id] || []
            return (
              <div key={route.id}>
                {routeIdx > 0 && (
                  <div
                    style={{ height: '1px', backgroundColor: '#F0F0F0', margin: '20px 0 16px' }}
                  />
                )}
                <div
                  style={{
                    backgroundColor: '#F4F5F9',
                    borderRadius: '8px',
                    padding: '12px 14px',
                    marginBottom: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#212427',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {place(route.origin)}
                  </span>
                  <span style={{ fontSize: '14px', color: '#667085', flexShrink: 0 }}>⇄</span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '14px',
                      color: '#667085',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      textAlign: 'right',
                    }}
                  >
                    {place(route.destination)}
                  </span>
                </div>

                {rows.map((row, idx) => (
                  <div key={idx} style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr auto',
                        gap: '8px',
                        alignItems: 'end',
                        marginBottom: '8px',
                      }}
                    >
                      <div>
                        <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#667085' }}>
                          {t.common.carType}
                          <RequiredMark />
                        </p>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={row.vehicleType}
                            onChange={(e) =>
                              updateRow(route.id, idx, 'vehicleType', e.target.value)
                            }
                            style={{
                              width: '100%',
                              padding: '10px 28px 10px 12px',
                              border: '1px solid #D6DAE0',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'Poppins, sans-serif',
                              appearance: 'none' as any,
                              WebkitAppearance: 'none' as any,
                              backgroundColor: '#fff',
                              cursor: 'pointer',
                              color: row.vehicleType ? '#212427' : '#667085',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                          >
                            <option value="">{t.oneWay.select}</option>
                            {availableCarTypes.map((vt) => (
                              <option key={vt} value={vt}>
                                {vt}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none',
                              color: '#667085',
                              fontSize: '11px',
                            }}
                          >
                            ▾
                          </span>
                        </div>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#667085' }}>
                          {t.oneWay.price}
                          <RequiredMark />
                        </p>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'stretch',
                            border: '1px solid #D6DAE0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <input
                            type="number"
                            placeholder="0.00"
                            value={row.price}
                            onChange={(e) => updateRow(route.id, idx, 'price', e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            style={{
                              flex: 1,
                              padding: '10px 8px 10px 12px',
                              border: 'none',
                              fontSize: '14px',
                              fontFamily: 'Poppins, sans-serif',
                              outline: 'none',
                              minWidth: '0',
                            }}
                          />
                          <span
                            style={{
                              padding: '0 10px',
                              fontSize: '12px',
                              color: '#667085',
                              flexShrink: 0,
                              borderLeft: '1px solid #F0F0F0',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#FAFAFA',
                            }}
                          >
                            THB
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => rows.length > 1 && removeRow(route.id, idx)}
                        style={{
                          width: '38px',
                          height: '38px',
                          border: '1px solid #D6DAE0',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          cursor: rows.length > 1 ? 'pointer' : 'default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          color: rows.length > 1 ? '#ef4444' : '#D6DAE0',
                          flexShrink: 0,
                        }}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                    {idx === rows.length - 1 && (
                      <div
                        style={{
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'flex-start',
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#667085' }}>ⓘ</span>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#667085',
                            margin: 0,
                            lineHeight: '1.5',
                          }}
                        >
                          {t.common.netRevenueNote}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                <button
                  onClick={() => addRow(route.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0048B3',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px 0 8px',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {t.oneWay.addCarType}
                </button>
              </div>
            )
          })}

          {pricingError && (
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '8px 0 0' }}>{pricingError}</p>
          )}
        </div>
        <BottomNav
          onBack={() => setView(editingId ? 'list' : 'route-select')}
          onNext={handleConfirmPricing}
          nextLabel={t.common.confirm}
        />
      </div>
    )
  }

  // ── Delete Confirmation Modal ────────────────────────────────────────
  if (confirmDeleteId) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px',
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '320px',
            width: '100%',
            textAlign: 'center',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <p style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 8px' }}>
            {t.oneWay.deleteRouteTitle}
          </p>
          <p style={{ fontSize: '14px', color: '#667085', margin: '0 0 24px', lineHeight: '1.5' }}>
            {t.oneWay.deleteRouteDesc}
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setConfirmDeleteId(null)}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '24px',
                border: '1px solid #D6DAE0',
                backgroundColor: '#fff',
                color: '#667085',
                fontSize: '15px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              {t.common.cancel}
            </button>
            <button
              onClick={confirmDelete}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '24px',
                border: 'none',
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '15px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              {t.common.delete}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── List (main) ──────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#fff',
        minHeight: '100vh',
        maxWidth: '500px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <TopBar onExit={onExit} />
      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        title={t.oneWay.title}
        currentStepProgress={localRoutes.length > 0 ? 100 : 0}
      />
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        {localRoutes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <p style={{ fontSize: '15px', fontWeight: '600', color: '#212427', margin: '0 0 4px' }}>
              {t.oneWay.noRoutes}
            </p>
            <p style={{ fontSize: '13px', color: '#667085', margin: 0 }}>{t.oneWay.noRoutesHint}</p>
          </div>
        )}
        {localRoutes.map((r) => (
          <div
            key={r.id}
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px',
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#212427',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {place(r.origin)}
              </span>
              <span style={{ fontSize: '14px', color: '#667085', flexShrink: 0 }}>⇄</span>
              <span
                style={{
                  flex: 1,
                  fontSize: '14px',
                  color: '#667085',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                }}
              >
                {place(r.destination)}
              </span>
            </div>
            {r.vehiclePrices
              .filter((vp) => vp.price !== null)
              .map((vp) => (
                <div
                  key={vp.vehicleType}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '4px',
                  }}
                >
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1zjzpxvw6prTsEgTxSLJLqwXKGp3W3eNtYibTcRtPAo3gM6zcTTTLZConyV8eQsTEMfJbyJu8N9mQpCFpKxua2W4.png"
                    alt="car"
                    style={{ width: '14px', height: '14px', objectFit: 'contain', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '13px', color: '#667085' }}>
                    {vp.vehicleType} | THB {vp.price?.toLocaleString()}
                  </span>
                </div>
              ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={() => handleEdit(r)} style={pillBtnStyle('#0048B3', true)}>
                <EditOutlined style={{ fontSize: '13px' }} /> {t.common.edit}
              </button>
              <button onClick={() => handleDelete(r.id)} style={pillBtnStyle('#ef4444', false)}>
                <DeleteOutlined style={{ fontSize: '13px' }} /> {t.common.delete}
              </button>
            </div>
          </div>
        ))}
        {listError && (
          <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 8px' }}>{listError}</p>
        )}
        <button
          onClick={() => {
            setSelectedIds([])
            setSelectError('')
            setView('route-select')
          }}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#EFF6FF',
            color: '#0048B3',
            border: '1px dashed #0048B3',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '15px',
            fontFamily: 'Poppins, sans-serif',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '20px',
          }}
        >
          {t.oneWay.addRoute}
        </button>
      </div>
      <BottomNav
        onBack={onBack}
        onNext={handleListNext}
        nextDisabled={localRoutes.length === 0}
        loading={submitting}
      />
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
}

const pillBtnStyle = (color: string, filled: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  borderRadius: '20px',
  border: `1px solid ${color}`,
  backgroundColor: filled ? color : '#fff',
  color: filled ? '#fff' : color,
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: '500',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
})

const addMoreBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0048B3',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  padding: '4px 0 8px',
  fontFamily: 'Poppins, sans-serif',
}

const mapDeleteBtnStyle: React.CSSProperties = {
  width: '42px',
  height: '42px',
  border: '1px solid #FECACA',
  borderRadius: '8px',
  backgroundColor: '#FEF2F2',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  color: '#ef4444',
  flexShrink: 0,
}

export default CarWithDriverStep3OneWay
